import * as React from 'react';

// * MUI
import {
  alpha,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { formatNumber } from '@/utils/formatters';

// * Icons
import ModelTrainingRoundedIcon from '@mui/icons-material/ModelTrainingRounded';
import FolderRoundedIcon from '@mui/icons-material/FolderRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import QueryStatsRoundedIcon from '@mui/icons-material/QueryStatsRounded';

import StateChip from './helpers';

// * Queries
import { usePutEnergyInitiativeMutation } from '@/api/recalcV2/endpoints/energyInitiativesApiSlice';

// * Redux
import { showSnackbar } from '@/redux/snackbarSlice';
import { useDispatch } from 'react-redux';

import { DateTime } from 'luxon';

import InitiativeKanbanCardActions from './InitiativeKanbanCardActions';

// * i18n
import { useTranslation } from 'react-i18next';
import CardHeaderWithIcon from '@/components/card/CardHeaderWithIcon';

const getTrendColor = (percentage) => {
  if (percentage >= 0) {
    return 'success'; // Green for positive savings
  } else {
    return 'error'; // Red for negative savings
  }
};

const formatDateRange = (startISO, endISO) => {
  if (!startISO && !endISO) return '—';

  const formatter = new Intl.DateTimeFormat('da-DK', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

  if (startISO && !endISO) return `${formatter.format(new Date(startISO))} –`;
  if (!startISO && endISO) return `– ${formatter.format(new Date(endISO))}`;

  const start = new Date(startISO);
  const end = new Date(endISO);

  const sameDay =
    start.getFullYear() === end.getFullYear() &&
    start.getMonth() === end.getMonth() &&
    start.getDate() === end.getDate();

  const sameMonth =
    start.getFullYear() === end.getFullYear() &&
    start.getMonth() === end.getMonth();

  const sameYear = start.getFullYear() === end.getFullYear();

  const dayMonthFormatter = new Intl.DateTimeFormat('da-DK', {
    day: 'numeric',
    month: 'short',
  });

  const fullFormatter = formatter;

  if (sameDay) return fullFormatter.format(start);
  if (sameMonth) return `${start.getDate()}.–${fullFormatter.format(end)}`;
  if (sameYear)
    return `${dayMonthFormatter.format(start)} – ${fullFormatter.format(end)}`;
  return `${fullFormatter.format(start)} – ${fullFormatter.format(end)}`;
};

/**
 * API initiative shape (doc only)
 * @typedef {Object} ApiInitiative
 * @property {number} energyInitiativeId
 * @property {string} name
 * @property {string|null} baselineStart
 * @property {string|null} baselineEnd
 * @property {string|null} comparisonStart
 * @property {string|null} comparisonEnd
 * @property {Array|null} data
 * // ... other fields ignored by the kanban
 */

function toApiTS(input) {
  if (!input) return null;

  let dt;
  if (input instanceof Date) dt = DateTime.fromJSDate(input);
  else if (typeof input === 'number') dt = DateTime.fromMillis(input);
  else dt = DateTime.fromISO(String(input), { setZone: true });

  if (!dt.isValid) return null;
  return dt.toUTC().toISO(); // ← this is the Zulu string your API expects
}

/** Map server → UI shape */
function mapServerToUI(srv) {
  return {
    id: srv.energyInitiativeId,
    name: srv.name || `Initiative ${srv.energyInitiativeId}`,
    serverId: srv.energyInitiativeId, // keep numeric id for API calls
    title: srv.name,
    // normalized ISO strings or null
    description: srv.description || '',
    state: srv.state || 'Created',
    completed: srv.completed ? toApiTS(srv.completed) : null,
    baselineStart: srv.baselineStart || null,
    baselineEnd: srv.baselineEnd || null,
    comparisonStart: srv.comparisonStart || null,
    comparisonEnd: srv.comparisonEnd || null,
    preferredMode: srv.preferredModel || 0,
    data: srv.energySavings || 0,
    energyTreeNodes: Array.isArray(srv.energyTreeNodes)
      ? srv.energyTreeNodes
      : [],
    // keep full main server object for edits
    actualCO2eEmission: srv.energySavings?.AdvancedModelUsed
      ? srv.energySavings?.AdvancedEnergySavings?.CO2eSavingsSummary
          ?.ActualCO2eEmission
      : srv.energySavings?.BasicEnergySavings?.CO2eSavingsSummary
          ?.ActualCO2eEmission,
    co2eSavings:
      srv.energySavings?.BasicEnergySavings?.CO2eSavingsSummary?.CO2eSavings,
    co2eSavingsPercentage: srv.energySavings?.AdvancedModelUsed
      ? srv.energySavings?.AdvancedEnergySavings?.CO2eSavingsSummary
          ?.CO2eSavingsPercentage
      : srv.energySavings?.BasicEnergySavings?.CO2eSavingsSummary
          ?.CO2eSavingsPercentage,
    averageBaselineEnergyUsage:
      srv.energySavings?.BasicEnergySavings?.EnergySavingsSummary
        ?.AverageBaselineEnergyUsage,
    averageComparisonEnergyUsage:
      srv.energySavings?.BasicEnergySavings?.EnergySavingsSummary
        ?.AverageComparisonEnergyUsage,
    energySavings: srv.energySavings?.AdvancedModelUsed
      ? srv.energySavings?.AdvancedEnergySavings?.EnergySavingsSummary
          ?.EnergySavings
      : srv.energySavings?.BasicEnergySavings?.EnergySavingsSummary
          ?.EnergySavings,
    energySavingsPercentage: srv.energySavings?.AdvancedModelUsed
      ? srv.energySavings?.AdvancedEnergySavings?.EnergySavingsSummary
          ?.EnergySavingsPercentage
      : srv.energySavings?.BasicEnergySavings?.EnergySavingsSummary
          ?.EnergySavingsPercentage,
    estimatedEnergySavingsToDate: srv.energySavings?.AdvancedModelUsed
      ? srv.energySavings?.ForecastedSavings?.AdvEstimatedTotalSavings
      : srv.energySavings?.ForecastedSavings?.BasicEstimatedTotalSavings,
    advancedSavingsAvailable: srv.energySavings?.AdvancedModelUsed,
    advancedEnergySavings: srv.energySavings?.AdvancedEnergySavings,
    main: srv,
    // keep anything else you need from the server here...
  };
}

function uiDateChangesToApiPatch(changes = {}) {
  if (!changes || typeof changes !== 'object') return {};
  const patch = {};
  const has = (k) => Object.prototype.hasOwnProperty.call(changes, k);
  if (has('baselineStart'))
    patch.baselineStart = changes.baselineStart
      ? toApiTS(changes.baselineStart)
      : null;
  if (has('baselineEnd'))
    patch.baselineEnd = changes.baselineEnd
      ? toApiTS(changes.baselineEnd)
      : null;
  if (has('comparisonStart'))
    patch.comparisonStart = changes.comparisonStart
      ? toApiTS(changes.comparisonStart)
      : null;
  if (has('comparisonEnd'))
    patch.comparisonEnd = changes.comparisonEnd
      ? toApiTS(changes.comparisonEnd)
      : null;
  return patch;
}

// Build the FULL PUT body the endpoint requires by merging prior server body + date patch.
function buildPutBody(prevRaw = {}, uiChanges = {}) {
  const datePatch = uiDateChangesToApiPatch(uiChanges);
  const next = { ...prevRaw, ...datePatch };

  if (Object.prototype.hasOwnProperty.call(datePatch, 'comparisonEnd')) {
    next.completed = datePatch.comparisonEnd ?? prevRaw.completed ?? null;
  }

  const norm = (v) => (v ? toApiTS(v) : null);

  const newState = deriveState(next);

  return {
    ...prevRaw,
    name: next.name ?? '',
    description: next.description ?? '',
    state: newState,
    completed: norm(next.completed),
    baselineStart: norm(next.baselineStart),
    baselineEnd: norm(next.baselineEnd),
    comparisonStart: norm(next.comparisonStart),
    comparisonEnd: norm(next.comparisonEnd),
    preferredModel:
      typeof next.preferredModel === 'number' ? next.preferredModel : 0,
    energyTreeNodes: Array.isArray(next.energyTreeNodes)
      ? next.energyTreeNodes
      : [],
  };
}

/** Compute only the date fields that actually changed */
function diffDateFields(prev, next) {
  const out = {};
  const keys = [
    'baselineStart',
    'baselineEnd',
    'comparisonStart',
    'comparisonEnd',
  ];
  keys.forEach((k) => {
    if (prev[k] !== next[k]) out[k] = next[k] ?? null;
  });
  return out;
}

function deriveState(ini) {
  if (
    ini.baselineStart &&
    ini.baselineEnd &&
    ini.comparisonStart &&
    ini.comparisonEnd
  )
    return 'Done';
  if (ini.baselineStart && ini.baselineEnd && ini.comparisonStart)
    return 'Active';
  if (ini.baselineStart && ini.baselineEnd) return 'Initialized';
  if (ini.baselineStart) return 'Created';
  return 'Created';
}

const fromApi = (api) => ({
  id: String(api.energyInitiativeId),
  serverId: api.energyInitiativeId,
  title: api.name ?? `Initiative ${api.energyInitiativeId}`,
  baselineStart: toApiTS(api.baselineStart),
  baselineEnd: toApiTS(api.baselineEnd),
  comparisonStart: toApiTS(api.comparisonStart),
  comparisonEnd: toApiTS(api.comparisonEnd),
  data: api.energySavings ?? null,
  actualCO2eEmission: api.energySavings?.AdvancedModelUsed
    ? api.energySavings?.AdvancedEnergySavings?.CO2eSavingsSummary
        ?.ActualCO2eEmission
    : api.energySavings?.BasicEnergySavings?.CO2eSavingsSummary
        ?.ActualCO2eEmission,
  actualEnergyUsage: api.energySavings?.AdvancedModelUsed
    ? api.energySavings?.AdvancedEnergySavings?.EnergySavingsSummary
        ?.ActualEnergyUsage
    : api.energySavings?.BasicEnergySavings?.EnergySavingsSummary
        ?.ActualEnergyUsage,
  co2eSavings: api.energySavings?.AdvancedModelUsed
    ? api.energySavings?.AdvancedEnergySavings?.CO2eSavingsSummary?.CO2eSavings
    : api.energySavings?.BasicEnergySavings?.CO2eSavingsSummary?.CO2eSavings,
  co2eSavingsPercentage: api.energySavings?.AdvancedModelUsed
    ? api.energySavings?.AdvancedEnergySavings?.CO2eSavingsSummary
        ?.CO2eSavingsPercentage
    : api.energySavings?.BasicEnergySavings?.CO2eSavingsSummary
        ?.CO2eSavingsPercentage,
  averageBaselineEnergyUsage:
    api.energySavings?.BasicEnergySavings?.EnergySavingsSummary
      ?.AverageBaselineEnergyUsage,
  averageComparisonEnergyUsage:
    api.energySavings?.BasicEnergySavings?.EnergySavingsSummary
      ?.AverageComparisonEnergyUsage,
  energySavings: api.energySavings?.AdvancedModelUsed
    ? api.energySavings?.AdvancedEnergySavings?.EnergySavingsSummary
        ?.EnergySavings
    : api.energySavings?.BasicEnergySavings?.EnergySavingsSummary
        ?.EnergySavings,
  energySavingsPercentage: api.energySavings?.AdvancedModelUsed
    ? api.energySavings?.AdvancedEnergySavings?.EnergySavingsSummary
        ?.EnergySavingsPercentage
    : api.energySavings?.BasicEnergySavings?.EnergySavingsSummary
        ?.EnergySavingsPercentage,
  estimatedEnergySavingsToDate: api.energySavings?.AdvancedModelUsed
    ? api.energySavings?.ForecastedSavings?.AdvEstimatedTotalSavings
    : api.energySavings?.ForecastedSavings?.BasicEstimatedTotalSavings,
  estimatedCO2eSavingsToDate: api.energySavings?.AdvancedModelUsed
    ? api.energySavings?.ForecastedSavings?.AdvEstimatedTotalSavingsCO2e
    : api.energySavings?.ForecastedSavings?.BasicEstimatedTotalSavingsCO2e,
  advancedSavingsAvailable: api.energySavings?.AdvancedModelUsed,
  main: {
    id: api.energyInitiativeId,
    name: api.name ?? '',
    description: api.description ?? '',
    state: api.state ?? 'Created',
    completed: api.completed ?? null,
    baselineStart: api.baselineStart ?? null,
    baselineEnd: api.baselineEnd ?? null,
    comparisonStart: api.comparisonStart ?? null,
    comparisonEnd: api.comparisonEnd ?? null,
    preferredModel: api.preferredModel ?? 0,
    energySavings: api.energySavings ?? 0,
    energyTreeNodes: Array.isArray(api.energyTreeNodes)
      ? api.energyTreeNodes
      : [],
  },
});

export default function InitiativeKanban({
  initiatives = [],
  account,
  handleViewInitiative,
  handleEditInitiative,
  handleDeleteInitiative,
}) {
  const dispatch = useDispatch();
  const [items, setItems] = React.useState(() =>
    (initiatives ?? []).map(mapServerToUI),
  );

  const [editing, setEditing] = React.useState(null); // Initiative | null
  const [savingIds, setSavingIds] = React.useState(() => new Set());

  const [putEnergyInitiative, { isLoading }] = usePutEnergyInitiativeMutation();

  React.useEffect(() => {
    setItems(initiatives.map(fromApi));
  }, [initiatives]);

  const nowISO = () => toApiTS(new Date());

  /** Optimistic persist that rolls back on error */
  const persistDates = async (id, changes = {}) => {
    // no-op if nothing to change
    if (
      !changes ||
      typeof changes !== 'object' ||
      Object.keys(changes).length === 0
    )
      return;

    // Find current row snapshot (pre-optimistic)
    const prevRow = items.find((it) => it.id === id);
    if (!prevRow) {
      console.warn('persistDates: item not found for id', id);
      dispatch(
        showSnackbar({
          severity: 'error',
          message: 'Kan ikke finde initiativet.',
        }),
      );
      return;
    }

    setSavingIds((s) => new Set(s).add(id));

    // Apply optimistic UI
    setItems((prev) =>
      prev.map((it) => (it.id === id ? { ...it, ...changes } : it)),
    );

    // Build FULL PUT body from prev server body + our date changes
    const fullBody = buildPutBody(prevRow.main, changes);
    const newState = deriveState(fullBody);

    try {
      await putEnergyInitiative({
        accountId: account?.AccountId ?? account?.id,
        energyInitiativeId: prevRow.serverId ?? Number(prevRow.id),
        updates: { ...fullBody, state: newState }, // PUT expects the full object in the body
      }).unwrap();

      // Keep our cached server copy in sync after success
      setItems((prev) =>
        prev.map((it) => (it.id === id ? { ...it, main: fullBody } : it)),
      );

      dispatch(
        showSnackbar({
          severity: 'success',
          message: 'Datoer gemt.',
        }),
      );
    } catch (err) {
      // rollback
      setItems((prev) => prev.map((it) => (it.id === id ? prevRow : it)));
      dispatch(
        showSnackbar({
          severity: 'error',
          message:
            err?.data?.message ||
            err?.message ||
            'Kunne ikke gemme datoer (save failed).',
        }),
      );
    } finally {
      setSavingIds((s) => {
        const n = new Set(s);
        n.delete(id);
        return n;
      });
    }
  };
  // Serial actions (now call persistDates)
  const startBaseline = (id) => persistDates(id, { baselineStart: nowISO() });
  const endBaseline = (id) => persistDates(id, { baselineEnd: nowISO() });
  const implementationDone = (id) =>
    persistDates(id, { comparisonStart: nowISO() });
  const complete = (id) => persistDates(id, { comparisonEnd: nowISO() });

  // Clearing all dates (send nulls for all four)
  const resetDates = (id) =>
    persistDates(id, {
      baselineStart: null,
      baselineEnd: null,
      comparisonStart: null,
      comparisonEnd: null,
    });

  const columns = [
    { key: 'Planned', stateKeys: ['Created', 'Initialized'] },
    { key: 'InProgress', stateKeys: ['Active', 'Done'] },
    { key: 'Archived', stateKeys: ['Archived'] },
  ];

  const grouped = {
    Planned: items.filter((i) =>
      ['Created', 'Initialized'].includes(i.main.state),
    ),
    InProgress: items.filter((i) => ['Active', 'Done'].includes(i.main.state)),
    Archived: items.filter((i) => i.main.state === 'Archived'),
  };

  return (
    <Box sx={{ p: 0 }}>
      <Grid container spacing={2}>
        {columns.map((col) => (
          <Grid item xs={12} sm={6} md={4} key={col.key}>
            <Column
              stateKeys={col.stateKeys}
              items={grouped[col.key]}
              onStartBaseline={startBaseline}
              onEndBaseline={endBaseline}
              onImplementationDone={implementationDone}
              onComplete={complete}
              onReset={resetDates}
              onView={(ini) => handleViewInitiative(ini)}
              onEdit={(ini) => handleEditInitiative(ini)}
              onDelete={(ini) => handleDeleteInitiative(ini)}
              savingIds={savingIds}
              isLoading={isLoading}
            />
          </Grid>
        ))}
      </Grid>

      {editing && (
        <EditDialog
          initiative={editing}
          onClose={() => setEditing(null)}
          onSave={(next) => {
            const delta = diffDateFields(editing, next);
            if (Object.keys(delta).length > 0) {
              persistDates(editing.id, delta);
            }
            setEditing(null);
          }}
        />
      )}
    </Box>
  );
}

function Column({
  stateKeys,
  items,
  onStartBaseline,
  onEndBaseline,
  onImplementationDone,
  onComplete,
  onEdit,
  onView,
  onDelete,
  onReset,
  savingIds,
  isLoading,
}) {
  const { t } = useTranslation('energyxplorer-initiatives-tab');
  const icon = (stateKeys || []).includes('Created') ? (
    <AutoAwesomeRoundedIcon />
  ) : (stateKeys || []).includes('Active') ? (
    <QueryStatsRoundedIcon />
  ) : (
    <FolderRoundedIcon />
  );
  const title = (stateKeys || []).includes('Created')
    ? 'Opstartet'
    : (stateKeys || []).includes('Active')
      ? 'Aktive'
      : 'Rapportklar';
  return (
    <Box
      sx={{
        bgcolor: 'background.default',
        borderRadius: 2,
        border: (theme) => `1px solid ${theme.palette.divider}`,
        minHeight: 300,
        p: 1,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1, p: 1 }}
      >
        <CardHeaderWithIcon title={title} icon={icon} />
      </Stack>
      <Stack spacing={1}>
        {items.length === 0 && (
          <Box
            sx={{
              p: 2,
              color: 'text.secondary',
              justifyContent: 'center',
              alignItems: 'center',
              display: 'flex',
              textAlign: 'center',
              verticalAlign: 'center',
              height: 300,
            }}
          >
            {t('kanban_columns.no_initiatives_found')}: {title}.
          </Box>
        )}
        {items.map((i) => (
          <InitiativeCard
            key={i.id}
            data={i}
            onStartBaseline={onStartBaseline}
            onEndBaseline={onEndBaseline}
            onImplementationDone={onImplementationDone}
            onComplete={onComplete}
            onEdit={onEdit}
            onView={onView}
            onReset={onReset}
            onDelete={onDelete}
            saving={savingIds.has(i.id)}
            isLoading={isLoading}
          />
        ))}
      </Stack>
    </Box>
  );
}

function InitiativeCard({
  data,
  onStartBaseline,
  onEndBaseline,
  onImplementationDone,
  onComplete,
  onReset,
  onView,
  onDelete,
  onEdit,
  isLoading,
}) {
  // const state = deriveState(data.main.state);
  const savingsData = data || {};
  const state = data.main.state;
  const showStartBaseline = !data.baselineStart;
  const showEndBaseline = !!data.baselineStart && !data.baselineEnd;
  const showImplementationDone = !!data.baselineEnd && !data.comparisonStart; // serial step into Ongoing
  const showComplete = !!data.comparisonStart && !data.comparisonEnd;

  // display values with fallbacks
  const estimatedSavingsToDate = savingsData.estimatedEnergySavingsToDate || 0;
  const estimatedCO2eSavingsToDate =
    savingsData.estimatedCO2eSavingsToDate || 0;
  const energySavings = savingsData.energySavings || 0;
  const energySavingsPercentage = savingsData.energySavingsPercentage || 0;
  const co2eSavings = savingsData.co2eSavings || 0;
  const co2eSavingsPercentage = savingsData.co2eSavingsPercentage || 0;

  const { t } = useTranslation('energyxplorer-initiatives-tab');

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 2,
        backgroundColor: (theme) =>
          theme.palette.mode === 'dark' ? '#212121' : 'background.paper',
      }}
    >
      <CardHeader
        title={
          <>
            <ListItem disableGutters sx={{ p: 0 }}>
              <ListItemIcon>
                {data.advancedSavingsAvailable ? (
                  <Tooltip title="Projekteret forbrug tilgængelig.">
                    <Avatar
                      variant="rounded"
                      sx={{
                        bgcolor: alpha('#c2dd00', 0.1),
                        mr: 1,
                        borderRadius: 3,
                      }}
                    >
                      <ModelTrainingRoundedIcon
                        sx={(theme) => ({
                          color: '#96b100',
                        })}
                      />
                    </Avatar>
                  </Tooltip>
                ) : (
                  <Avatar
                    variant="rounded"
                    sx={{
                      bgcolor: 'transparent',
                      mr: 1,
                      borderRadius: 3,
                    }}
                  >
                    <ModelTrainingRoundedIcon
                      sx={(theme) => ({
                        color:
                          theme.palette.mode === 'light' ? 'lightgray' : 'gray',
                      })}
                    />
                  </Avatar>
                )}
              </ListItemIcon>
              <ListItemText
                primary={data.title}
                secondary={data.main?.description || ''}
              />
            </ListItem>
          </>
        }
        action={
          <Stack direction="row" spacing={1} alignItems="center">
            <StateChip state={state} />
            <Tooltip title="Edit dates">
              <InitiativeKanbanCardActions
                onReset={() => onReset(data.id)}
                onView={() => onView(data)}
                onDelete={() => onDelete(data)}
                onEdit={() => onEdit(data.main)}
              />
            </Tooltip>
          </Stack>
        }
        sx={{ pb: 0.5 }}
      />
      <CardContent>
        <Stack spacing={1}>
          <Divider flexItem sx={{ my: 0.5 }} />

          <Row
            label={t('kanban_cards.baseline_period')}
            value={formatDateRange(data.baselineStart, data.baselineEnd)}
          />
          <Row
            label={t('kanban_cards.comparison_period')}
            value={formatDateRange(data.comparisonStart, data.comparisonEnd)}
          />

          <Stack
            direction="row"
            spacing={1}
            sx={{ flexWrap: 'wrap', justifyContent: 'space-between' }}
          >
            {showStartBaseline && (
              <Button
                size="small"
                variant="contained"
                onClick={() => onStartBaseline(data.id)}
                disabled={isLoading}
              >
                {state === 'planned' ? 'Start' : 'Start baseline'}
              </Button>
            )}
            {showEndBaseline && (
              <Button
                size="small"
                variant="contained"
                onClick={() => onEndBaseline(data.id)}
                disabled={isLoading}
              >
                Afslut baseline
              </Button>
            )}
            {showImplementationDone && (
              <Button
                size="small"
                variant="outlined"
                onClick={() => onImplementationDone(data.id)}
                disabled={isLoading}
              >
                Implementering færdig
              </Button>
            )}
            {showComplete && (
              <Button
                size="small"
                variant="outlined"
                color="success"
                onClick={() => onComplete(data.id)}
                disabled={isLoading}
              >
                Færdig
              </Button>
            )}
          </Stack>
        </Stack>
        {energySavings !== null && state === 'Archived' && (
          <Divider flexItem sx={{ my: 1 }} />
        )}

        <Box
          display="flex"
          alignItems="center"
          width="100%"
          height="100%"
          justifyContent="space-between"
          mt={2}
        >
          {estimatedSavingsToDate && state === 'Archived' ? (
            <>
              <Typography variant="caption" color="text.secondary">
                Fremskr. besparelse til dato:{' '}
              </Typography>
              <Typography variant="body2">{`${formatNumber(estimatedSavingsToDate)} kWh`}</Typography>
              {/* <Chip
                variant="filled"
                sx={{
                  marginLeft: 1,
                  borderRadius: 2,
                  backgroundColor: (theme) =>
                    alpha(
                      theme.palette[getTrendColor(energySavingsPercentage)]
                        .main,
                      0.1,
                    ),
                  color: (theme) =>
                    theme.palette.mode === 'light'
                      ? theme.palette[getTrendColor(energySavingsPercentage)]
                          .dark
                      : theme.palette[getTrendColor(energySavingsPercentage)]
                          .light,
                }}
                label={`${energySavingsPercentage.toFixed(1)}%` || '0%'}
                size="small"
              />{' '} */}
            </>
          ) : (
            ''
          )}
        </Box>

        <Box
          display="flex"
          alignItems="center"
          width="100%"
          height="100%"
          justifyContent="space-between"
          mt={1}
        >
          {estimatedCO2eSavingsToDate && state === 'Archived' ? (
            <>
              <Typography variant="caption" color="text.secondary">
                Fremskr. CO2e besparelse til dato:{' '}
              </Typography>
              <Typography variant="body2">{`${formatNumber(estimatedCO2eSavingsToDate / 1000)} kg CO2e`}</Typography>
              {/* <Chip
                variant="filled"
                sx={{
                  marginLeft: 1,
                  borderRadius: 2,
                  backgroundColor: (theme) =>
                    alpha(
                      theme.palette[getTrendColor(co2eSavingsPercentage)].main,
                      0.1,
                    ),
                  color: (theme) =>
                    theme.palette.mode === 'light'
                      ? theme.palette[getTrendColor(co2eSavingsPercentage)].dark
                      : theme.palette[getTrendColor(co2eSavingsPercentage)]
                          .light,
                }}
                label={`${co2eSavingsPercentage.toFixed(1)}%`}
                size="small"
              />{' '} */}
            </>
          ) : (
            ''
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

function Row({ label, value }) {
  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="baseline"
      justifyContent={'space-between'}
    >
      <Typography
        variant="caption"
        sx={{ minWidth: 100, color: 'text.secondary' }}
      >
        {label}
      </Typography>
      <Typography variant="caption">{value}</Typography>
    </Stack>
  );
}

function EditDialog({ initiative, onClose, onSave }) {
  const [local, setLocal] = React.useState({ ...initiative });

  const handleChange = (key) => (e) => {
    const val = e.target.value || null;
    setLocal((prev) => ({ ...prev, [key]: val }));
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Rediger {initiative.title}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <DateField
            label="Baseline start"
            value={local.baselineStart}
            onChange={handleChange('baselineStart')}
          />
          <DateField
            label="Baseline end"
            value={local.baselineEnd}
            onChange={handleChange('baselineEnd')}
          />
          <Divider sx={{ my: 1 }} />
          <DateField
            label="Comparison start"
            value={local.comparisonStart}
            onChange={handleChange('comparisonStart')}
          />
          <DateField
            label="Comparison end"
            value={local.comparisonEnd}
            onChange={handleChange('comparisonEnd')}
          />
          <Typography variant="caption" color="text.secondary">
            Tip: Leave a field empty to clear it. Values are stored as ISO
            strings.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => onSave(local)}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function DateField({ label, value, onChange }) {
  // Use native datetime-local for simplicity (no extra deps)
  const toLocalInput = (iso) => {
    if (!iso) return '';
    const dt = DateTime.fromISO(iso, { setZone: true });
    return dt.isValid ? dt.toLocal().toFormat("yyyy-LL-dd'T'HH:mm") : '';
  };
  const fromLocalInput = (val) =>
    val
      ? DateTime.fromISO(val, { zone: 'local' })
          .toUTC()
          .startOf('second')
          .toFormat("yyyy-LL-dd'T'HH:mm:ssZZ")
      : '';

  return (
    <TextField
      label={label}
      type="datetime-local"
      value={toLocalInput(value)}
      onChange={(e) => {
        const iso = fromLocalInput(e.target.value);
        // store empty string as null through parent handler
        e.target.value = iso;
        onChange(e);
      }}
      InputLabelProps={{ shrink: true }}
      fullWidth
      size="small"
    />
  );
}
